import { isActiveStatus } from './etaProjection';
import { useWorkingStatusPreference } from '../../contexts/WorkingStatusPreferenceContext';
import { routeCheckIsFresh, type RouteCascadeEstimate } from './routeEtaCascade';

/** Independent Samsara arrival; never feeds the existing ETA or risk colors. */
export interface RouteEtaData {
  route_id?: string | null;
  eta: string | null;
  status: string;
  updated_at: string | null;
  timezone: string;
  origin_timezone?: string;
  stop_minutes?: number;
  provider_status?: string;
  travel_minutes_by_origin?: Record<number, number>;
}

const STATUS_LABELS: Record<string, string> = {
  scheduled: 'Awaiting departure',
  unassigned: 'Unassigned',
  'en route': 'Pending',
  pending: 'Pending',
  arrived: 'Arrived',
  departed: 'GPS departed',
  awaiting_start: 'Awaiting move start',
  awaiting_tracking: 'Awaiting Samsara tracking',
  progress_mismatch: 'Progress mismatch',
  skipped: 'Skipped',
  missing_schedule: 'Needs schedule',
  awaiting_projection: 'Awaiting route plan',
  missing_assignment: 'No Samsara driver',
  missing_location: 'Needs location',
  started_before_tracking: 'Not tracked',
  route_changed: 'Route changed',
  unavailable: 'Unavailable',
  stale: 'Stale',
};

function RouteEtaLabel({ routeId, title, heading, value }: {
  routeId?: string | null; title: string; heading: string; value: string;
}) {
  const className = 'inline-flex flex-wrap items-baseline gap-1 text-[0.6875rem] tabular-nums text-sky-700 dark:text-sky-300';
  const content = <><span className="font-medium">{heading}</span><span>{value}</span></>;
  const id = routeId?.trim();
  if (!id) return <span className={className} title={title}>{content}</span>;
  return <a href={`https://cloud.samsara.com/o/39743/fleet/dispatch/v2/route/${encodeURIComponent(id)}`}
    target="_blank" rel="noopener noreferrer" draggable={false}
    onClick={event => event.stopPropagation()}
    className={`${className} hover:underline focus-visible:underline`}
    title={`${title} · Open route in Samsara (new tab)`}>
    {content}<span aria-hidden="true">↗</span>
  </a>;
}

export function RouteEta({ moveStatus, data, cascade, side, now = new Date() }: {
  moveStatus: string | undefined;
  data?: RouteEtaData | null; cascade?: RouteCascadeEstimate;
  side?: 'origin' | 'destination'; now?: Date;
}) {
  const { showSamsaraRouteEtas } = useWorkingStatusPreference();
  if (!showSamsaraRouteEtas || !isActiveStatus(moveStatus ?? '')) return null;
  if (cascade) {
    const target = side || cascade.target;
    const zone = target === 'origin' ? cascade.originTimezone : cascade.destinationTimezone;
    const instant = new Date(cascade[target]);
    let label = 'Stale';
    if (routeCheckIsFresh(cascade.updatedAt, now)) {
      try {
        label = instant.toLocaleTimeString('en-US', { timeZone: zone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
        if (instant.toLocaleDateString('en-CA', { timeZone: zone }) !== now.toLocaleDateString('en-CA', { timeZone: zone })) {
          label = `${instant.toLocaleDateString('en-US', { timeZone: zone, month: 'short', day: 'numeric' })} ${label}`;
        }
        if (cascade.awaitingUpdate) label += ' (awaiting update)';
      } catch { label = 'Unavailable'; }
    }
    return <RouteEtaLabel routeId={cascade.anchorRouteId}
      title={`Projected arrival at ${target} from Samsara route ${cascade.anchorPronumber}-${cascade.anchorLegnum}, plus dispatch stop and travel estimates. Last checked ${new Date(cascade.updatedAt).toLocaleString()}${cascade.awaitingUpdate ? ' · Based on the last reported estimate; awaiting an updated Samsara ETA. This does not confirm arrival.' : ''}`}
      heading={`Route projection (${target === 'origin' ? 'orig' : 'dest'}):`} value={label} />;
  }
  if (side === 'origin') return null;
  if (!data) return null;
  let label = STATUS_LABELS[data.status] ?? 'Unavailable';
  let title = 'Samsara Route ETA to destination';
  if (data.status === 'awaiting_tracking') {
    title += ' · FastCMS has recorded departure. Manually record the origin departure in Samsara if automatic tracking does not start.';
  }
  if (data.provider_status && data.provider_status !== data.status && ['awaiting_start', 'scheduled', 'arrived', 'progress_mismatch'].includes(data.status)) {
    title += ` · Samsara reports ${data.provider_status}; display follows FastCMS move progress`;
  }
  const updated = data.updated_at ? new Date(data.updated_at).getTime() : NaN;
  const stale = !routeCheckIsFresh(data.updated_at, now);
  if (data.status === 'en route' && data.eta) {
    const arrival = new Date(data.eta);
    if (stale) label = 'Stale';
    else if (!Number.isFinite(arrival.getTime())) label = 'Unavailable';
    else {
      try {
        const timeZone = data.timezone || 'America/Chicago';
        const day = (date: Date) => date.toLocaleDateString('en-CA', { timeZone });
        label = arrival.toLocaleTimeString('en-US', { timeZone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' });
        if (day(arrival) !== day(now)) {
          label = `${arrival.toLocaleDateString('en-US', { timeZone, month: 'short', day: 'numeric' })} ${label}`;
        }
        title += `: ${arrival.toLocaleString('en-US', { timeZone, timeZoneName: 'short' })}`;
        if (arrival.getTime() < now.getTime()) {
          label += ' (awaiting update)';
          title += ' · Last reported estimate; awaiting an updated Samsara ETA. This does not confirm arrival.';
        }
      } catch { label = 'Unavailable'; }
    }
  }
  if (Number.isFinite(updated)) title += ` · Last checked ${new Date(updated).toLocaleString()}`;
  return <RouteEtaLabel routeId={data.route_id} title={title} heading="Samsara Route:" value={label} />;
}
